import { SUPPORTED_LOCALES, I18N_FALLBACK_LOCALE, getLocaleLabel, resolveSupportedLocale } from '@withjoy/locale';

export type i18nLanguage = {
  name: string;
  /**
   * The endonym split into its parts, for the two-line language picker:
   * `nativeLanguage` over `nativeRegion` (e.g. "Français" / "Canada").
   *
   * Both come from `@withjoy/locale`'s curated table rather than being parsed
   * out of `name` — the split is not derivable by string-splitting, and the
   * pair is what disambiguates: `nativeRegion` alone is NOT unique (Canada is
   * the region for both en-CA and fr-CA), while language+region is.
   */
  nativeLanguage: string;
  nativeRegion: string;
  /** BCP 47 locale code — used both as directory name and runtime code. */
  languageCode: string;
  /** @deprecated Use languageCode. Retained for backward compatibility during migration. */
  localePath: string;
  isDefault?: boolean;
};

/**
 * marcom's language set, derived from the org-wide locale registry.
 *
 * Per the i18n registry contract (see joylifeinc/translate
 * docs/superpowers/specs/2026-08-20-6bc-consolidation-audit.md), the supported
 * set is not maintained here: it comes from `@withjoy/locale`, and display
 * names come from that package's curated `LOCALE_LABELS` (endonym register —
 * a reader who does not read English still has to recognize their language in
 * the menu). Adding a locale org-wide is therefore a version bump, not an edit
 * to this file.
 *
 * NOTE for anyone widening the registry: marcom builds ONE Gatsby site per
 * language, so a new locale also needs its build-matrix entry. The matrices in
 * .github/workflows/build-n-deploy-*.yml are generated from this same list —
 * keep them generated, never hand-listed.
 */
export const languages: i18nLanguage[] = SUPPORTED_LOCALES.map((languageCode) => ({
  name: getLocaleLabel(languageCode),
  nativeLanguage: getLocaleLabel(languageCode, 'language'),
  nativeRegion: getLocaleLabel(languageCode, 'region'),
  languageCode,
  localePath: languageCode,
  ...(languageCode === I18N_FALLBACK_LOCALE ? { isDefault: true } : {}),
}));

const languagesByLocale = new Map(languages.map((lang) => [lang.languageCode, lang]));

const defaultLanguage: i18nLanguage = languagesByLocale.get(I18N_FALLBACK_LOCALE) ?? languages[0];

export const BUILD_LANGUAGE = typeof __BUILD_LOCALE__ !== 'undefined' ? languagesByLocale.get(__BUILD_LOCALE__)?.languageCode : undefined;

export const DEFAULT_LANGUAGE = defaultLanguage.languageCode;

/**
 * The language i18next falls back to when a key is missing from the active one.
 *
 * This MUST NOT be the build locale. Each production build runs with
 * `__BUILD_LOCALE__` set to the locale being built, so deriving this from
 * `BUILD_LANGUAGE` made `fallbackLng` self-referential — an en-CA build fell
 * back to en-CA, which is not a fallback at all. Missing keys then surfaced as
 * the raw key string, and because `returnObjects: true` is set globally, every
 * call site that treats a key as an array (`t('valueProps').map(...)`) threw
 * during SSR instead of degrading to English.
 *
 * `BUILD_LANGUAGE` remains exported for consumers that genuinely want "which
 * site is this"; it is deliberately not used here.
 *
 * It resolves only during SSR. `__BUILD_LOCALE__` is defined for `build-html`
 * alone and MUST stay that way: all twelve locale builds share one
 * `assetPrefix`, which only works while their client bundles are byte-identical
 * (see gatsby/webpackConfig.ts). Client code that needs the build's locale
 * should read i18next's initialised language, which comes from per-locale
 * page-data — see `servableLanguages` in @hooks/useGeoLocale.
 */
export const FALLBACK_LANGUAGE = DEFAULT_LANGUAGE;

/**
 * Resolves an arbitrary language code to one of marcom's built languages.
 *
 * Delegates matching to `@withjoy/locale`'s registry resolver, which the whole
 * org shares: exact match, case/separator normalization, closest regional
 * sibling (`de-AT` → `de-DE`), bare base code (`de` → `de-DE`), and the
 * product-intent aliases in `LOCALE_TRANSLATION_MAP` (`en-IE` → `en-GB`,
 * `en-NZ` → `en-AU`). Unrecognized input falls back to en-US.
 *
 * The previous hand-rolled matcher had no alias support, so an `en-IE` visitor
 * was served American English rather than British.
 */
export const resolveLanguage = (extendedLanguageCode?: string): i18nLanguage => {
  const resolved = resolveSupportedLocale(extendedLanguageCode ?? null);
  return languagesByLocale.get(resolved) ?? defaultLanguage;
};
